import { Injectable, inject } from "@angular/core";
import { default as regionsNamesCa } from "@eaws/micro-regions_names/ca.json";

import "./mergeFeatureCollections";
import { default as regionsNamesDe } from "@eaws/micro-regions_names/de.json";
import { default as regionsNamesEn } from "@eaws/micro-regions_names/en.json";
import { default as regionsNamesEs } from "@eaws/micro-regions_names/es.json";
import { default as regionsNamesFr } from "@eaws/micro-regions_names/fr.json";
import { default as regionsNamesIt } from "@eaws/micro-regions_names/it.json";
import { default as regionsNamesOc } from "@eaws/micro-regions_names/oc.json";
import outline_properties from "@eaws/outline_properties/index.json";
import { TranslateService } from "@ngx-translate/core";
import { LanguageSchema } from "app/models/text.model";
import { FeatureCollection, MultiPolygon, Geometry } from "geojson";
import { from, map, Observable } from "rxjs";
import { z } from "zod/v4";

import aggregatedRegions from "../../../assets/aggregated_regions.json";
import { AuthenticationService } from "../authentication-service/authentication.service";
import { getRegionForLatLng, initAugmentRegion } from "./augmentRegion";
import { loadRegions } from "./regions-loader.mjs";

@Injectable({ providedIn: "root" })
export class RegionsService {
  private translateService = inject(TranslateService);
  private authenticationService = inject(AuthenticationService);

  private eawsRegions = z.array(RegionOutlineSchema).parse(outline_properties, { reportInput: true });

  eawsRegion(id: string) {
    return this.eawsRegions.find((r) => r.id === id);
  }

  // Level 1 regions: parts of provinces
  getLevel1Regions(id: string): string[] {
    return aggregatedRegions.level1.find((ids) => ids.includes(id)) ?? [];
  }

  // Level 2 regions: provinces
  getLevel2Regions(id: string): string[] {
    return aggregatedRegions.level2.find((ids) => ids.includes(id)) ?? [];
  }

  async getRegionsAsync(): Promise<FeatureCollection<MultiPolygon, RegionProperties>> {
    const regions = await loadRegions();
    return this.translateNames(regions);
  }

  async getActiveServerRegionsAsync(): Promise<FeatureCollection<MultiPolygon, RegionProperties>> {
    if (!this.authenticationService.getActiveRegionId()) {
      return undefined;
    }
    const regions = await this.getRegionsAsync();
    return {
      type: "FeatureCollection",
      features: regions.features.filter((f) => this.authenticationService.isActiveRegion(f.properties.id)),
    };
  }

  async getInternalServerRegionsAsync(): Promise<FeatureCollection<MultiPolygon, RegionProperties>> {
    const regions = await this.getRegionsAsync();
    return {
      type: "FeatureCollection",
      features: regions.features.filter((f) => this.authenticationService.isInternalRegion(f.properties.id)),
    };
  }

  getRegionNames(): typeof regionsNamesDe {
    switch (this.translateService.getCurrentLang()) {
      case "de":
        return regionsNamesDe;
      case "it":
        return regionsNamesIt;
      case "en":
        return regionsNamesEn;
      case "fr":
        return regionsNamesFr;
      case "es":
        return regionsNamesEs;
      case "ca":
        return regionsNamesCa;
      case "oc":
        return regionsNamesOc;
      default:
        return regionsNamesEn;
    }
  }

  private translateNames<T extends Geometry>(data: FeatureCollection<T, RegionProperties>) {
    data.features.forEach((feature) => (feature.properties.name = this.getRegionName(feature.properties.id)));
    return data;
  }

  getRegionName(id: string): string {
    return this.getRegionNames()[id] ?? id;
  }

  findRegionForCoordinates(lat: number, lng: number): Observable<string | null> {
    return from(initAugmentRegion()).pipe(map(() => getRegionForLatLng({ latitude: lat, longitude: lng }) ?? null));
  }
}

export interface RegionProperties {
  id: string;
  name?: string;
}

export interface RegionWithElevationProperties extends RegionProperties {
  elevation: "high" | "low" | "low_high";
  threshold?: number;
}

export const RegionOutlineSchema = z.object({
  id: z.string(),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  aws: z.array(
    z.object({
      name: z.string(),
      url: z
        .object({
          "api:lang": z.array(z.string()).optional(),
        })
        .catchall(
          z.union([
            LanguageSchema,
            z.enum([
              "api",
              "api:date",
              "facebook",
              "flickr",
              "instagram",
              "linkedin",
              "mail",
              "telegram",
              "whatsapp",
              "twitter",
              "youtube",
            ]),
            z.string(),
          ]),
        ),
    }),
  ),
});

export type RegionOutlineProperties = z.infer<typeof RegionOutlineSchema>;
